import { type RefObject, useCallback, useEffect, useState } from 'react';

/**
 * Pełny ekran, blokada wygaszania i tryb immersyjny.
 *
 * Trzy rzeczy naraz, bo w praktyce zawsze chodzą razem: widz przechodzi na pełny
 * ekran, ekran nie ma prawa zgasnąć w trakcie odcinka, a chrome aplikacji ma
 * zniknąć. Rozdzielenie ich na trzy hooki dawałoby trzy miejsca do zsynchronizowania.
 *
 * Nic tutaj nie jest wymagane do działania odtwarzacza — każda z tych rzeczy
 * może być niedostępna (Safari nie ma `wakeLock`, iframe może nie mieć
 * uprawnienia do pełnego ekranu) i wtedy po prostu jej nie ma. Dlatego wszystko
 * jest opcjonalne i żadna porażka nie jest zgłaszana widzowi.
 */

export interface ImmersiveState {
  isFullscreen: boolean;
  toggleFullscreen(): void;
}

export function useImmersive({
  containerRef,
  videoRef,
  active,
}: {
  /** Element wchodzący na pełny ekran — kontener, nie samo wideo. */
  containerRef: RefObject<HTMLElement | null>;
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Czy odtwarzacz w ogóle działa; przy `false` zwalniamy zasoby. */
  active: boolean;
}): ImmersiveState {
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Stan pełnego ekranu może się zmienić bez naszego udziału — klawiszem Escape
  // albo gestem systemu. Czytamy go z dokumentu, nie z własnej flagi.
  useEffect(() => {
    function onChange(): void {
      setIsFullscreen(document.fullscreenElement !== null);
    }
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  /**
   * Blokada wygaszania ekranu w trakcie odtwarzania.
   *
   * Trzymana tylko wtedy, gdy materiał NAPRAWDĘ leci — nie przez cały czas
   * otwartej karty. Blokada podczas pauzy albo przy zatrzymanym pobieraniu
   * trzymałaby ekran włączony bez powodu i zjadała baterię.
   *
   * Zwolnienie musi być tolerancyjne na wyjątki: przeglądarka sama zwalnia
   * blokadę przy ukryciu karty, więc nasze `release()` bywa wtedy nieaktualne.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!active || video === null) return;

    let sentinel: { release(): Promise<void> } | null = null;
    let disposed = false;

    const request = (): void => {
      if (sentinel !== null || disposed) return;
      const wakeLock = (
        navigator as Navigator & {
          wakeLock?: { request(kind: 'screen'): Promise<{ release(): Promise<void> }> };
        }
      ).wakeLock;
      if (wakeLock === undefined) return;
      void wakeLock
        .request('screen')
        .then((lock) => {
          if (disposed) void lock.release().catch(() => undefined);
          else sentinel = lock;
        })
        .catch(() => undefined);
    };

    const release = (): void => {
      const lock = sentinel;
      sentinel = null;
      void lock?.release().catch(() => undefined);
    };

    if (!video.paused) request();
    video.addEventListener('play', request);
    video.addEventListener('pause', release);
    video.addEventListener('ended', release);

    return () => {
      disposed = true;
      release();
      video.removeEventListener('play', request);
      video.removeEventListener('pause', release);
      video.removeEventListener('ended', release);
    };
  }, [active, videoRef]);

  /**
   * Znacznik na `<html>`, po którym `styles.css` zdejmuje kursor.
   *
   * Atrybut, nie klasa: kolejna reguła nie musi wtedy znać nazw klas aplikacji,
   * a selektory `html[data-immersive="true"]` są już w arkuszu.
   */
  useEffect(() => {
    if (!isFullscreen) {
      delete document.documentElement.dataset.immersive;
      return;
    }
    document.documentElement.dataset.immersive = 'true';
    return () => {
      delete document.documentElement.dataset.immersive;
    };
  }, [isFullscreen]);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (container === null) return;
    if (document.fullscreenElement !== null) {
      void document.exitFullscreen().catch(() => undefined);
      return;
    }
    /**
     * Na pełny ekran wchodzi KONTENER, nie element wideo.
     *
     * Gdyby wchodziło samo wideo, wszystko rysowane nad nim — warstwa napisów
     * libass, przycisk pomijania, wybór ścieżki — zostałoby za spodem obrazu.
     * To dokładnie ten błąd, który wyszedł po dodaniu napisów: sterowanie
     * znikało w momencie, w którym jest najbardziej potrzebne.
     */
    void container.requestFullscreen().catch(() => undefined);
  }, [containerRef]);

  return { isFullscreen, toggleFullscreen };
}

/**
 * Czy zdarzenie klawiatury pochodzi z pola tekstowego.
 *
 * Bez tego spacja wpisana w czacie pokoju zatrzymywałaby odcinek wszystkim,
 * a litera `f` wrzucała na pełny ekran w środku zdania.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable) ||
    /**
     * Pasek przewijania obsługuje strzałki SAM.
     *
     * Bez tego wyjątku skrót globalny i obsługa paska zadziałałyby razem, więc
     * jedno naciśnięcie przewijałoby o dwadzieścia sekund zamiast o dziesięć —
     * błąd niewidoczny w kodzie, bo każda ze stron jest osobno poprawna.
     */
    (target instanceof HTMLElement && target.closest('[data-slot="seek-bar"]') !== null)
  );
}
